import * as valid from "lib/valid"
import React, { useCallback } from "react"
import { Controller, Control, Path, FieldValues, RegisterOptions } from "react-hook-form"
import * as result from "lib/result"
import * as filterEnum from "enums/filterEnum"
import { DropdownFilter, FormCardContext } from "components"
import * as opt from "lib/opt"

export interface FormDropdownFilterProps<
	T extends FieldValues,
	FieldType extends string,
	I,
	O extends object,
	V extends O[] | O | null = O | null,
> {
	name: Path<T>
	control: Control<T>
	label?: string
	filterType: filterEnum.FilterEnum<FieldType, I, O>
	keyFunc: (item: O) => string
	labelFunc?: (item: O) => string
	rules?: Omit<RegisterOptions<T, Path<T>>, "valueAsNumber" | "valueAsDate" | "setValueAs">
	/** When false, the label will not show the required asterisk (e.g. Edit Sector Admin). When undefined, derived from rules. */
	required?: boolean
	multiple?: boolean
	disabled?: boolean
	autoTrigger?: boolean
	onDataLoaded?: (data: O[]) => void
	renderError?: (err: Result<void, string>) => React.ReactNode
	onChange?: (props: { strValue: () => string; field: FieldType; value: V }) => void
	staticOptions?: O[]
	placeholder?: string
	getOptionDisabled?: (option: O) => boolean
	getOptionClassName?: (option: O) => string | undefined
	groupBy?: (option: O) => string
}

export function FormDropdownFilter<
	T extends FieldValues,
	FieldType extends string,
	I,
	O extends object,
	V extends O[] | O | null = O | null,
>({
	name,
	control,
	label,
	filterType,
	keyFunc,
	labelFunc,
	rules,
	required: requiredProp,
	multiple,
	disabled,
	autoTrigger,
	onDataLoaded,
	renderError,
	onChange,
	staticOptions,
	placeholder,	
	getOptionDisabled,
	getOptionClassName,
	groupBy,
	...restProps
}: FormDropdownFilterProps<T, FieldType, I, O, V>): React.JSX.Element {
	const { hideLabel } = React.useContext(FormCardContext)

	return (
		<Controller
			name={name}
			control={control}
			rules={{
				...rules,
				validate: (value) => {
					const originalValidate = rules?.validate

					// First check user-defined validation if provided
					if (originalValidate) {
						if (valid.IsFunc(originalValidate)) {
							const result = (originalValidate as any)(value)
							if (result !== true) {
								return result
							}
						} else if (valid.IsObject(originalValidate)) {
							// Handle object of validators
							for (const key in originalValidate) {
								if (Object.prototype.hasOwnProperty.call(originalValidate, key)) {
									const validator = originalValidate[key]
									const result = (validator as any)(value)
									if (result !== true) {
										return result
									}
								}
							}
						}
					}

					// For required fields, handle empty arrays inside Some
					if (rules?.required) {
						const requiredMessage = valid.IsString(rules.required)
							? rules.required
							: "This field is required"

						// Handle Option types
						if (opt.IsOpt(value)) {
							if (value.IsNone()) {
								return requiredMessage
							}

							if (value.IsSome()) {
								const unwrapped = value.Unwrap()

								// For array values (multi-select), check if empty
								if (Array.isArray(unwrapped) && unwrapped.IsEmpty()) {
									return requiredMessage
								}
							}
						}
						// Direct value check (shouldn't normally happen with Options)
						else if (isNil(value)) {
							return requiredMessage
						} else if (Array.isArray(value) && (value as any[]).IsEmpty()) {
							return requiredMessage
						}
					}

					return true
				},
			}}
			render={({ field, fieldState }) => {
				const { onChange: fieldOnChange, value } = field

				// Get value for dropdown
				const getValue = useCallback(() => {
					if (opt.IsOpt(value)) {
						if (value.IsSome()) {
							return value.Unwrap()
						}
						return null
					}
					return value as V
				}, [value, multiple])

				// Handle dropdown change
				const handleChange = useCallback(
					(props: { strValue: () => string; field: FieldType; value: V }) => {
						// For multi-select, dont wrap it in option
						fieldOnChange(
							Array.isArray(props.value) ? props.value : opt.FromNullable(props.value)
						)

						if (onChange) {
							onChange(props)
						}
					},
					[fieldOnChange, onChange, multiple]
				)

				// Error message handling
				const errorMessage = fieldState.error?.message

				return (
					<>
						<DropdownFilter
							{...restProps}
							filterType={filterType}
							autoTrigger={autoTrigger}
							disabled={disabled}
							getValue={getValue}
							keyFunc={keyFunc}
							labelFunc={labelFunc}
							required={requiredProp !== undefined ? requiredProp : !rules?.IsEmpty()}
							label={label}
							hideLabel={hideLabel}
							onChange={handleChange}
							onDataLoaded={onDataLoaded}
							staticOptions={staticOptions}
							multiple={multiple}
							errorMsg={renderError ? undefined : errorMessage}
							textFieldProps={{
								error: !!errorMessage,
								placeholder: placeholder,
							}}			
							getOptionDisabled={getOptionDisabled}
							getOptionClassName={getOptionClassName}
							groupBy={groupBy}
						/>
						{renderError &&
							renderError(errorMessage ? result.Err(errorMessage) : result.Ok())}
					</>
				)
			}}
		/>
	)
}
